import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';
import EmailComposer from '../shared/email-composer';
import sanitizeHtml from '../../utils/sanitize-html';

/**
 * Step 1 of the bulk message creation wizard: Message Content.
 * Renders sender details, the email content editor, and a live desktop/mobile preview.
 */
const BulkMessageContentStep = ({
  senderExpanded,
  setSenderExpanded,
  senderEmailPrefix,
  setSenderEmailPrefix,
  orgDomain,
  senderName,
  setSenderName,
  replyToEmail,
  setReplyToEmail,
  contentExpanded,
  setContentExpanded,
  subject,
  setSubject,
  bodyText,
  setBodyText,
  setAppliedTemplate,
  files,
  removeFile,
  onImageUpload,
  onFileUpload,
  outreachTemplates,
  prePopulated,
  previewMode,
  setPreviewMode,
  appliedSignature,
}) => (
  <div className="bulk-message-content-step">
    <div className="bulk-message-content-step--settings">
      <h4 className="bulk-message-content-step--section-title">EMAIL SETTINGS</h4>

      {/* Sender Details Section */}
      <div className="bulk-message-content-step--section">
        <button
          type="button"
          className="bulk-message-content-step--section-header"
          onClick={() => setSenderExpanded(!senderExpanded)}
        >
          <div className="bulk-message-content-step--section-header-left">
            <Icon icon="person" className="bulk-message-content-step--section-icon" />
            <span>Sender Details</span>
          </div>
          <Icon
            icon={senderExpanded ? 'chevron-up' : 'chevron-down'}
            className="create-cadence-configure--card-toggle-icon"
          />
        </button>
        {senderExpanded && (
          <div className="bulk-message-content-step--section-content">
            <div className="bulk-message-content-step--field">
              <label className="bulk-message-content-step--label">SENDER EMAIL</label>
              <div className="bulk-message-content-step--split-email">
                <input
                  type="text"
                  value={senderEmailPrefix}
                  onChange={(e) => setSenderEmailPrefix(e.target.value)}
                  placeholder="outreach"
                  className="bulk-message-content-step--input bulk-message-content-step--split-email-prefix"
                />
                <span className="bulk-message-content-step--split-email-at">@</span>
                <span className="bulk-message-content-step--split-email-domain">{orgDomain}</span>
              </div>
              {senderEmailPrefix.includes('@') ? (
                <span className="bulk-message-content-step--error">
                  Don&apos;t include @ or a domain here — enter only the part before the @. Your sending domain is
                  fixed and shown above.
                </span>
              ) : (
                <span className="bulk-message-content-step--hint">
                  Enter only the part before the @ — your sending domain is fixed and shown above.
                </span>
              )}
            </div>
            <div className="bulk-message-content-step--field">
              <label className="bulk-message-content-step--label">SENDER NAME</label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="Your Name"
                className="bulk-message-content-step--input"
              />
              <span className="bulk-message-content-step--hint">
                This is who it will look like sent the email. Defaults to your name.
              </span>
            </div>
            <div className="bulk-message-content-step--field">
              <label className="bulk-message-content-step--label">REPLY-TO EMAIL</label>
              <input
                type="text"
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                placeholder="you@example.com"
                className="bulk-message-content-step--input"
              />
              <span className="bulk-message-content-step--hint">
                Who will receive replies. Defaults to your email. Separate multiple addresses with commas.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Email Content Section */}
      <div className="bulk-message-content-step--section">
        <button
          type="button"
          className="bulk-message-content-step--section-header"
          onClick={() => setContentExpanded(!contentExpanded)}
        >
          <div className="bulk-message-content-step--section-header-left">
            <Icon icon="email" className="bulk-message-content-step--section-icon" />
            <span>Email Content</span>
          </div>
          <Icon
            icon={contentExpanded ? 'chevron-up' : 'chevron-down'}
            className="create-cadence-configure--card-toggle-icon"
          />
        </button>
        {contentExpanded && (
          <div className="bulk-message-content-step--section-content">
            <div className="bulk-message-content-step--field">
              <EmailComposer
                subject={subject}
                onSubjectChange={setSubject}
                body={bodyText}
                onBodyChange={setBodyText}
                onTemplateApply={({ id, templateName }) => setAppliedTemplate({ id, templateName })}
                files={files}
                removeFile={removeFile}
                onImageUpload={onImageUpload}
                onFileUpload={onFileUpload}
                outreachTemplates={outreachTemplates}
                editorKey={prePopulated ? 'edit' : 'create'}
                bodyPlaceholder="Dear [First Name],\n\nWe hope this message finds you well!..."
              />
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Email Preview Panel */}
    <div className="bulk-message-content-step--preview">
      <div className="bulk-message-content-step--preview-header">
        <h4 className="bulk-message-content-step--section-title">EMAIL PREVIEW</h4>
        <div className="bulk-message-content-step--preview-actions">
          <div className="bulk-message-content-step--preview-toggle">
            <button
              type="button"
              className={`bulk-message-content-step--preview-btn ${previewMode === 'desktop' ? 'is-active' : ''}`}
              onClick={() => setPreviewMode('desktop')}
            >
              <Icon icon="computer" />
              Desktop
            </button>
            <button
              type="button"
              className={`bulk-message-content-step--preview-btn ${previewMode === 'mobile' ? 'is-active' : ''}`}
              onClick={() => setPreviewMode('mobile')}
            >
              <Icon icon="smartphone" />
              Mobile
            </button>
          </div>
        </div>
      </div>

      <div className={`bulk-message-content-step--preview-frame ${previewMode === 'mobile' ? 'is-mobile' : ''}`}>
        <div className="bulk-message-content-step--preview-email">
          <div className="bulk-message-content-step--preview-from">
            <div className="bulk-message-content-step--preview-avatar">{(senderName || 'A')[0].toUpperCase()}</div>
            <div className="bulk-message-content-step--preview-from-info">
              <span className="bulk-message-content-step--preview-from-name">
                {senderName || 'Alumni Relations Team'}
              </span>
              <span className="bulk-message-content-step--preview-from-email">
                To: [Constituent Name] &lt;[Email Address]&gt;
              </span>
            </div>
            <span className="bulk-message-content-step--preview-date">Today, 10:30 AM</span>
          </div>

          <div className="bulk-message-content-step--preview-body">
            <h2 className="bulk-message-content-step--preview-subject">
              {subject || "You're invited to a special Thank You Evening at GivingTree University"}
            </h2>
            <div
              className="bulk-message-content-step--preview-content"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  (bodyText ||
                    `<p>Dear [First Name],</p>
                    <p>We hope this message finds you well!</p>
                    <p>We're excited to let you know that we're putting together an exclusive event on Nov. 21, 2026 to thank our top donors for their generosity throughout 2026. Simply click the button below to learn more and RSVP.</p>
                    <p>This promises to be an unforgettable event, with special guests, campus tours, and opportunities to connect with students your donations have impacted throughout the year. We'd be honored if you joined us.</p>
                    <p>Best regards,<br/><strong>The Alumni Relations Team</strong></p>`) +
                    (appliedSignature?.signature_body
                      ? `<div style="margin-top:16px;border-top:1px solid #e0e0e0;padding-top:12px">${appliedSignature.signature_body}</div>`
                      : '')
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

BulkMessageContentStep.propTypes = {
  senderExpanded: PropTypes.bool.isRequired,
  setSenderExpanded: PropTypes.func.isRequired,
  senderEmailPrefix: PropTypes.string.isRequired,
  setSenderEmailPrefix: PropTypes.func.isRequired,
  orgDomain: PropTypes.string.isRequired,
  senderName: PropTypes.string.isRequired,
  setSenderName: PropTypes.func.isRequired,
  replyToEmail: PropTypes.string.isRequired,
  setReplyToEmail: PropTypes.func.isRequired,
  contentExpanded: PropTypes.bool.isRequired,
  setContentExpanded: PropTypes.func.isRequired,
  subject: PropTypes.string.isRequired,
  setSubject: PropTypes.func.isRequired,
  bodyText: PropTypes.string.isRequired,
  setBodyText: PropTypes.func.isRequired,
  setAppliedTemplate: PropTypes.func.isRequired,
  files: PropTypes.array.isRequired,
  removeFile: PropTypes.func.isRequired,
  onImageUpload: PropTypes.func.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  outreachTemplates: PropTypes.array,
  prePopulated: PropTypes.bool.isRequired,
  previewMode: PropTypes.string.isRequired,
  setPreviewMode: PropTypes.func.isRequired,
  appliedSignature: PropTypes.object,
};

export default BulkMessageContentStep;
